import { jwtDecode } from 'jwt-decode';
import type { JWTPayload, DecodedToken } from '@/types';
import { AuthenticationError } from '@/types';

/**
 * Extract JWT token from URL parameters
 */
export function extractTokenFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('token');
}

/**
 * Decode and validate JWT token
 */
export function decodeToken(token: string): DecodedToken {
  try {
    const decoded = jwtDecode<JWTPayload>(token);
    
    const now = Date.now() / 1000;
    const isExpired = decoded.exp < now;
    const isValid = !isExpired && Boolean(decoded.customerId);

    return {
      ...decoded,
      isValid,
      isExpired,
    };
  } catch (error) {
    throw new AuthenticationError('Invalid token format');
  }
}

/**
 * Validate token and return customer ID if valid
 */
export function validateToken(token: string | null): string | null {
  if (!token) {
    return null;
  }

  try {
    const decodedToken = decodeToken(token);
    
    if (!decodedToken.isValid) {
      throw new AuthenticationError('Token is invalid or expired');
    }

    return decodedToken.customerId;
  } catch (error) {
    console.error('Token validation failed:', error);
    return null;
  }
}

/**
 * Check if current token is expired
 */
export function isTokenExpired(token: string): boolean {
  try {
    const decoded = decodeToken(token);
    return decoded.isExpired;
  } catch {
    return true;
  }
}

/**
 * Get time until token expires (in milliseconds)
 */
export function getTokenTimeToExpiry(token: string): number {
  try {
    const decoded = decodeToken(token);
    const now = Date.now() / 1000;
    const timeLeft = decoded.exp - now;
    return Math.max(0, timeLeft * 1000);
  } catch {
    return 0;
  }
}

/**
 * Format token expiry time as human-readable string
 */
export function formatTokenExpiry(token: string): string {
  try {
    const decoded = decodeToken(token);
    const expiryDate = new Date(decoded.exp * 1000);
    return expiryDate.toLocaleString();
  } catch {
    return 'Invalid token';
  }
}

/**
 * Clear token from URL parameters without refreshing page
 */
export function clearTokenFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  window.history.replaceState({}, document.title, url.toString());
}

/**
 * Store token securely (in memory only for security)
 */
let tokenStore: string | null = null;

export function storeToken(token: string): void {
  tokenStore = token;
  clearTokenFromUrl();
}

export function getStoredToken(): string | null {
  return tokenStore;
}

export function clearStoredToken(): void {
  tokenStore = null;
}